/**
 * Asks which document type a file is and hands the answer back.
 *
 * Expects the name of the copy in the client's Files folder, so the person sees which row this is,
 * and the two callbacks that close the modal and take the type picked.
 *
 * Renders the heading, the line saying why the type is being asked for, the file name, one select
 * of document types, and Cancel and Parse document buttons; the second stays disabled until a type
 * is picked.
 *
 * DocumentsTable opens this from the Add document type button of a row whose last reading failed
 * with 'unknown_document_kind', because sending that document's id on its own would fail it the
 * same way again. What the person picks goes to the read route as its optional docType
 * (readTaxDocumentBodySchema in api/routers/validators/taxProfileValidator.ts), and
 * requestTaxDocumentExtraction writes that type onto the row and reads the file as that type in the
 * one request.
 *
 * The list is documentTypeOptions below, which the Add document modal draws its two document type
 * selects with too, so the same names are read in both places. OcrDocumentType.UNKNOWN is the one
 * entry left out here: it means the file is none of the types we read, and sendForReading in
 * api/src/taxProfile/dashboard/addDocuments.ts files such a document instead of sending it, so it
 * is not an answer this button could carry out.
 *
 * DocumentsTable renders this only while a row is being asked about, so the type picked last time
 * is gone when it opens again and this component needs no reset of its own.
 */

import { useState } from 'react';
import {
  isOcrDocumentType,
  OcrDocumentType,
  TAX_DOCUMENT_KIND_LABELS,
} from '@shared/constants/taxProfile';
import { GenericModal } from '@/components/Modal';
import { UNKNOWN_KIND_OPTION } from '@/pages/TaxProfile/wording';

const CHOOSE_TYPE_HEADING = 'Choose a document type';

/** The line under the heading, which says why the type is being asked for. */
const CHOOSE_TYPE_SENTENCE =
  'We could not tell what this file is. Choose the type it should be read as.';

/** The option showing while no type has been picked, which the button stays disabled on. */
const CHOOSE_TYPE_PLACEHOLDER = 'Select a document type';

/** The label of the select, and the label of the button that hands the type back. */
const CHOOSE_TYPE_LABEL = 'Document type';
const CHOOSE_TYPE_CONFIRM = 'Parse document';

/**
 * The <option>s of a document type select, one per OcrDocumentType in its order, each named by
 * TAX_DOCUMENT_KIND_LABELS. With includeUnknown, OcrDocumentType.UNKNOWN is among them, named
 * UNKNOWN_KIND_OPTION; without it, it is left out. The select's empty option is the caller's.
 */
export const documentTypeOptions = (includeUnknown: boolean) =>
  Object.values(OcrDocumentType)
    .filter((kind) => includeUnknown || kind !== OcrDocumentType.UNKNOWN)
    .map((kind) => (
      <option key={kind} value={kind}>
        {kind === OcrDocumentType.UNKNOWN
          ? UNKNOWN_KIND_OPTION
          : TAX_DOCUMENT_KIND_LABELS[kind]}
      </option>
    ));

export interface ChooseDocumentTypeModalProps {
  /** The name of the copy in the client's Files folder, so the person sees which row this is. */
  fileName: string;
  onClose: () => void;
  /** Called with the type the person picked. The page sends it with the read request. */
  onConfirm: (docType: OcrDocumentType) => void | Promise<void>;
}

export default function ChooseDocumentTypeModal({
  fileName,
  onClose,
  onConfirm,
}: ChooseDocumentTypeModalProps) {
  const [docType, setDocType] = useState<OcrDocumentType | ''>('');

  return (
    <GenericModal isOpen onClose={onClose}>
      <h3 className='font-bold text-lg mb-1'>{CHOOSE_TYPE_HEADING}</h3>
      <p className='text-sm mb-1'>{CHOOSE_TYPE_SENTENCE}</p>
      <p className='text-xs opacity-70 break-all mb-3'>{fileName}</p>

      <label className='form-control mb-4' htmlFor='read-document-type'>
        <span className='label-text'>{CHOOSE_TYPE_LABEL}</span>
        <select
          id='read-document-type'
          className='select select-bordered select-sm w-full'
          value={docType}
          onChange={(event) => {
            const chosen = event.target.value;
            setDocType(isOcrDocumentType(chosen) ? chosen : '');
          }}
        >
          <option value=''>{CHOOSE_TYPE_PLACEHOLDER}</option>
          {documentTypeOptions(false)}
        </select>
      </label>

      <div className='flex justify-end gap-2'>
        <button
          type='button'
          className='btn btn-ghost btn-sm'
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type='button'
          className='btn btn-accent btn-sm'
          disabled={docType === ''}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={() => {
            if (docType === '') return undefined;
            return onConfirm(docType);
          }}
        >
          {CHOOSE_TYPE_CONFIRM}
        </button>
      </div>
    </GenericModal>
  );
}
